const LINE_TOP_TOLERANCE_PX = 1;

type CharRect = {
  index: number;
  rect: DOMRect;
};

const isSameVisualLine = (a: number, b: number): boolean =>
  Math.abs(a - b) <= LINE_TOP_TOLERANCE_PX;

const getFirstTextNode = (element: HTMLElement): Text | null => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.textContent?.length
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  });

  // This only returns the first text node found inside the element.
  // If the element has nested text, like <span>Hello</span><span>world</span>,
  // only the first text node is measured.
  return walker.nextNode() as Text | null;
};

const getCharRect = (
  range: Range,
  textNode: Text,
  index: number
): DOMRect | null => {
  const getClientRects = (
    range as unknown as { getClientRects?: () => DOMRectList }
  ).getClientRects;

  if (typeof getClientRects !== 'function') {
    return null;
  }

  // Measure a single character so we can detect where visual line breaks occur.
  try {
    range.setStart(textNode, index);
    range.setEnd(textNode, index + 1);

    return getClientRects.call(range)[0] ?? null;
  } catch {
    // Some test environments do not fully implement range geometry APIs.
    return null;
  }
};

const getCharRects = (element: HTMLElement): CharRect[] => {
  const textNode = getFirstTextNode(element);

  if (!textNode?.length) {
    return [];
  }

  // Reuse one Range instance instead of creating a new Range per character.
  const range = document.createRange();
  const charRects: CharRect[] = [];

  for (let index = 0; index < textNode.length; index += 1) {
    const rect = getCharRect(range, textNode, index);

    // Some characters may not produce a rect, like certain whitespace.
    if (rect) {
      charRects.push({ index, rect });
    }
  }

  return charRects;
};

const addUniqueLineTop = (lineTops: number[], top: number): void => {
  // Browser layout can produce tiny decimal differences, so compare with tolerance.
  const alreadyTracked = lineTops.some((lineTop) =>
    isSameVisualLine(lineTop, top)
  );

  if (!alreadyTracked) {
    lineTops.push(top);
  }
};

export const measureFirstLineCharCount = (element: HTMLElement): number => {
  const charRects = getCharRects(element);

  if (!charRects.length) {
    return 0;
  }

  const firstLineTop = charRects[0].rect.top;

  // First character whose top differs from the first line marks the next visual line.
  const firstNonMatchingChar = charRects.find(
    ({ rect }) => !isSameVisualLine(rect.top, firstLineTop)
  );

  return firstNonMatchingChar?.index ?? charRects.length;
};

export const countVisualLines = (element: HTMLElement): number => {
  const lineTops: number[] = [];

  for (const { rect } of getCharRects(element)) {
    addUniqueLineTop(lineTops, rect.top);
  }

  return lineTops.length;
};
